import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import SVG from 'react-inlinesvg';
import personIcon from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import PageLayout from 'layouts/PageLayout/PageLayout';
import SourceDisplay from 'containers/SettingsModal/SettingsSourceDisplay/SettingsSourceDisplayContainer';
import Button from 'components/Button/Button';
import classes from './Onboarding.module.scss';
import { isChrome } from 'utils/browser-utils';

const chromeHelpLink = 'https://support.google.com/chrome/answer/2693767';

class Onboarding extends React.Component {
  componentDidMount() {
    this.props.toggleDoNotDisturb(true);
  }

  componentWillUnmount() {
    this.props.toggleDoNotDisturb(false);
  }

  onboardingText = () => { // eslint-disable-line consistent-return
    const hasVideo = this.props.videoDevices.length;
    const hasAudio = this.props.audioDevices.length;

    if (hasVideo && hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.instructions"
              defaultMessage="Let's review the way your camera and microphone work in calls."
            />
          </div>
          <div>
            <FormattedMessage
              id="components.onboarding.secondInstructions"
              defaultMessage="You can change these preferences at any time in Settings."
            />
            <span className={classes.iconCircle}>
              <SVG src={personIcon} data-testid="personIcon" />
            </span>
          </div>
        </div>);
    }

    if (!hasVideo && !hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noAccess"
              defaultMessage="We do not have access to your camera or microphone."
            />
          </div>
          {this.allowAccessMessage()}
        </div>);
    }

    if (hasVideo && !hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noMicAccess"
              defaultMessage={
                'We do not have access to your microphone. ' +
                'If you proceed with these settings, you will need to call in by phone to hear the audio.'
              }
            />
          </div>
          {this.allowAccessMessage()}
        </div>);
    }

    if (!hasVideo && hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noCameraAccess"
              defaultMessage="We do not have access to your camera. If you proceed with these settings, your calls will be audio only."
            />
          </div>
          {this.allowAccessMessage()}
        </div>);
    }
  };

  allowAccessMessage = () => {
    if (isChrome() && !window.desktopApp) {
      return (
        <div>
          <FormattedMessage
            id="components.onboarding.allowAccess"
            defaultMessage="For the best experience, go to your Chrome settings to allow access."
          />
          <a href={chromeHelpLink} className={classes.helpLink} rel="noopener noreferrer" target="_blank">
            <FormattedMessage
              id="components.onboarding.accessInstructionsLink"
              defaultMessage="Learn more."
            />
          </a>
        </div>);
    }

    return (
      <div>
        <FormattedMessage
          id="components.onboarding.allowAccessGeneric"
          defaultMessage="For the best experience, please allow access."
        />
      </div>);
  };

  render() {
    return (
      <div>
        <PageLayout className={this.props.showOnboarding ? '' : classes.hiddenCameraCheck} >
          <div>
            <section className={classes.onboardingContainer}>
              <h1>
                <FormattedMessage
                  id="components.onboarding.title"
                  defaultMessage="Set my Call Preferences"
                />
              </h1>
              { this.onboardingText() }
              <SourceDisplay
                permissionOnly={!this.props.showOnboarding}
              />
            </section>
            <Button
              onClick={() => { this.props.hideOnboarding(); }}
              style={{ minWidth: 304, padding: 12, width: 304, marginTop: 80 }}
            >
              <FormattedMessage
                id="components.onboarding.next"
                defaultMessage="Next"
              />
            </Button>
          </div>
        </PageLayout>
      </div>);
  }
}

Onboarding.propTypes = {
  audioDevices: PT.array,
  hideOnboarding: PT.func.isRequired,
  intl: PT.object,
  showOnboarding: PT.bool,
  toggleDoNotDisturb: PT.func,
  videoDevices: PT.array
};

export default injectIntl(Onboarding);
